import { EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import { useBrigadeList } from "../hooks/useBrigade";
import { useBrigadeStore } from "../store/brigade.store";
import type { Brigade } from "../types";

interface BrigadeTableProps {
	organizationId: string;
	page: number;
	limit: number;
	onPageChange: (page: number) => void;
}

export function BrigadeTable({ organizationId, page, limit, onPageChange }: BrigadeTableProps) {
	const navigate = useNavigate();
	const { data, isLoading } = useBrigadeList({ organizationId, page, limit });
	const openEditModal = useBrigadeStore((s) => s.openEditModal);

	const columns: ColumnsType<Brigade> = [
		{
			title: "№",
			key: "index",
			width: 60,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		{
			title: "Brigada nomi",
			dataIndex: "name",
			key: "name",
			ellipsis: true,
			render: (name: string, record: Brigade) => (
				<button
					type="button"
					className="text-info hover:underline font-medium bg-transparent border-0 p-0 cursor-pointer"
					onClick={() => navigate(`/brigades/${record.id}`)}
				>
					{name}
				</button>
			),
		},
		{
			title: "Rahbar",
			dataIndex: "leaderName",
			key: "leaderName",
			ellipsis: true,
			render: (name: string) => name ?? "—",
		},
		{
			title: "Hududlar",
			dataIndex: "territories",
			key: "territories",
			ellipsis: true,
			render: (territories?: Brigade["territories"]) =>
				territories && territories.length > 0 ? territories.map((t) => t.name).join(", ") : "—",
		},
		{
			title: "A'zolar soni",
			dataIndex: "memberCount",
			key: "memberCount",
			width: 120,
			align: "center",
			render: (count: number) => count ?? 0,
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			render: (_: unknown, record: Brigade) => (
				<Dropdown
					menu={{
						items: [
							{
								key: "edit",
								icon: <EditOutlined />,
								label: "Tahrirlash",
								onClick: () => openEditModal(record),
							},
						],
					}}
					trigger={["click"]}
					placement="bottomRight"
				>
					<Button type="text" icon={<MoreOutlined />} size="small" />
				</Dropdown>
			),
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={data?.data?.items}
			loading={isLoading}
			rowKey="id"
			pagination={tablePagination({
				page,
				limit,
				total: data?.data?.meta?.total,
				onChange: onPageChange,
				sizeChanger: false,
			})}
			size="middle"
		/>
	);
}
